<template>
	<el-dialog
		v-bind="$attrs"
		v-on="dialogListeners"
		:visible.sync="innerVisible"
		:title="title"
		:width="width"
		:append-to-body="appendToBody"
		:close-on-click-modal="closeOnClickModal"
		:class="['lcsj-dialog', customClass]"
		:top="top"
	>
		<!-- 头部附加信息（如会员名/卡号/状态） -->
		<div v-if="$slots.headerExtra" slot="title" class="lcsj-dialog__extra">
			<slot name="headerExtra"></slot>
		</div>

		<div class="lcsj-dialog__body" v-loading="loading">
			<!-- 错误/提示条 -->
			<div v-if="alertType && alertText" :class="['lcsj-dialog__alert', 'is-' + alertType]">
				<i :class="alertIcon"></i>
				<span>{{ alertText }}</span>
			</div>

			<slot></slot>
		</div>

		<!-- 底部固定汇总条（如充值后的余额合计） -->
		<div v-if="$slots.summary" class="lcsj-dialog__summary">
			<slot name="summary"></slot>
		</div>

		<div slot="footer" class="lcsj-dialog__footer">
			<slot name="footerLeft">
				<span v-if="footerHint" class="lcsj-dialog__hint">{{ footerHint }}</span>
			</slot>
			<div class="lcsj-dialog__footer-btns">
				<slot name="footer">
					<el-button v-if="cancelText" size="small" @click="handleCancel">{{ cancelText }}</el-button>
					<!-- confirm-text 传空字符串则不渲染确定按钮
					     （纯详情/预览类弹窗只有「关闭」，不需要确定） -->
					<el-button v-if="confirmText" type="primary" size="small" :loading="confirmLoading" :disabled="confirmDisabled" @click="$emit('confirm')">
						{{ confirmText }}
					</el-button>
				</slot>
			</div>
		</div>
	</el-dialog>
</template>

<script>
/**
 * 弹窗壳（设计系统壳组件）
 * 统一四段式结构：标题(含附加信息) → 内容 → 汇总条 → 底部动作。
 * 仅统一视觉与结构，不干预业务 slot 内容。
 *
 * 用法：
 *   <lcsj-dialog title="会员充值" width="720px" :visible.sync="vis"
 *                 confirm-text="确认充值" @confirm="submit">
 *     <template #headerExtra><span class="lcsj-pill lcsj-pill--info">张雨萌 · VIP-018</span></template>
 *     ...表单内容...
 *     <template #summary><div class="my-sum">充值后余额 ¥5,060</div></template>
 *     <template #footerLeft><span class="tip">充值后不可撤销</span></template>
 *   </lcsj-dialog>
 */
export default {
	name: "lcsj-dialog",
	inheritAttrs: false,
	// 与原 MyDialog 保持相同的 v-model 接口，确保打开时挂载内容及子组件 ref。
	model: { prop: "visible", event: "update:visible" },
	props: {
		visible: { type: Boolean, default: false },
		title: { type: String, default: "" },
		width: { type: String, default: "720px" },
		top: { type: String, default: "10vh" },
		appendToBody: { type: Boolean, default: true },
		closeOnClickModal: { type: Boolean, default: false },
		loading: { type: Boolean, default: false },
		confirmLoading: { type: Boolean, default: false },
		confirmDisabled: { type: Boolean, default: false },
		confirmText: {
			type: String,
			default: ""
		},
		cancelText: { type: String, default: "取消" },
		/** 底部左侧灰色提示，如"充值后不可撤销" */
		footerHint: { type: String, default: "" },
		/** 内置提示条类型：success / warning / danger / info */
		alertType: { type: String, default: "" },
		alertText: { type: String, default: "" },
		customClass: { type: String, default: "" }
	},
	computed: {
		dialogListeners() {
			// 关闭生命周期由 el-dialog 发出，避免同步 visible 时重复触发 close。
			const { "update:visible": updateVisible, confirm, cancel, ...listeners } = this.$listeners;
			return listeners;
		},
		innerVisible: {
			get() {
				return this.visible;
			},
			set(v) {
				this.$emit("update:visible", v);
			}
		},
		alertIcon() {
			const map = {
				success: "el-icon-success",
				warning: "el-icon-warning",
				danger: "el-icon-error",
				info: "el-icon-info"
			};
			return map[this.alertType] || "el-icon-info";
		}
	},
	methods: {
		handleCancel() {
			this.innerVisible = false;
			this.$emit("cancel");
		}
	}
};
</script>

<style scoped>
.lcsj-dialog__extra {
	display: inline-flex;
	align-items: center;
	margin-left: 10px;
	vertical-align: middle;
}

.lcsj-dialog__body {
	height: auto;
	max-height: none;
	overflow: visible;
}

.lcsj-dialog__alert {
	display: flex;
	align-items: center;
	gap: 7px;
	padding: 9px 12px;
	border-radius: var(--app-radius);
	border: 1px solid;
	margin-bottom: 14px;
	font-size: var(--app-font-base);
}

.lcsj-dialog__alert.is-success {
	background: var(--app-success-soft);
	border-color: var(--app-success-border);
	color: var(--app-success-text);
}

.lcsj-dialog__alert.is-warning {
	background: var(--app-warning-soft);
	border-color: var(--app-warning-border);
	color: var(--app-warning-text);
}

.lcsj-dialog__alert.is-danger {
	background: var(--app-danger-soft);
	border-color: var(--app-danger-border);
	color: var(--app-danger-text);
}

.lcsj-dialog__alert.is-info {
	background: var(--app-primary-soft);
	border-color: var(--app-primary-border);
	color: var(--app-primary-text);
}

.lcsj-dialog__summary {
	padding: 12px 18px;
	border-top: 1px solid var(--app-border);
	background: var(--app-surface-muted);
}

.lcsj-dialog__footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.lcsj-dialog__hint {
	font-size: var(--app-font-base);
	color: var(--app-text-muted);
}

.lcsj-dialog__footer-btns {
	display: flex;
	gap: 9px;
	margin-left: auto;
}
</style>
